import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [bootstrap, setBootstrap] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("zeferius_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [buyNow, setBuyNowState] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("zeferius_buynow") || "null");
    } catch {
      return null;
    }
  });

  const setBuyNow = useCallback((item) => {
    setBuyNowState(item);
    if (item) sessionStorage.setItem("zeferius_buynow", JSON.stringify(item));
    else sessionStorage.removeItem("zeferius_buynow");
  }, []);

  useEffect(() => {
    fetchBootstrap();
  }, []);

  function fetchBootstrap() {
    return api.get("/store/bootstrap").then((r) => setBootstrap(r.data)).catch(() => {});
  }

  const refreshBootstrap = useCallback(() => fetchBootstrap(), []);

  useEffect(() => {
    const token = localStorage.getItem("zeferius_token");
    if (!token) {
      setAuthChecked(true);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("zeferius_token"))
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    localStorage.setItem("zeferius_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const key = (i) => `${i.product_id}:${i.variation_id}`;
      const existing = prev.find((i) => key(i) === key(item));
      if (existing) {
        return prev.map((i) => (key(i) === key(item) ? { ...i, quantity: Math.min(i.quantity + item.quantity, 99) } : i));
      }
      return [...prev, item];
    });
    setCartOpen(true);
  }, []);

  const setQty = useCallback((product_id, variation_id, quantity) => {
    setCart((prev) =>
      quantity <= 0
        ? prev.filter((i) => !(i.product_id === product_id && i.variation_id === variation_id))
        : prev.map((i) => (i.product_id === product_id && i.variation_id === variation_id ? { ...i, quantity } : i))
    );
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const login = useCallback((data) => {
    localStorage.setItem("zeferius_token", data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("zeferius_token");
    setUser(null);
  }, []);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <StoreContext.Provider value={{
      bootstrap, refreshBootstrap, user, authChecked, login, logout,
      cart, addToCart, setQty, clearCart, cartOpen, setCartOpen, cartCount, cartSubtotal,
      buyNow, setBuyNow,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
